import React, { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';

// Add images for each word (make sure these images exist in your public or assets folder)
const wordList = [
  { word: 'CAT', letters: ['C', 'A', 'T', 'B', 'E'], img: '/image/cat.png' },
  { word: 'DOG', letters: ['D', 'O', 'G', 'A', 'Q'], img: '/image/dog.png' },
  { word: 'SUN', letters: ['S', 'U', 'N', 'M', 'P'], img: '/image/sun.png' },
  { word: 'CAR', letters: ['C', 'A', 'R', 'L', 'T'], img: '/image/car.png' },
  { word: 'PEN', letters: ['P', 'E', 'N', 'O', 'S'], img: '/image/pen.png' }
];

export const DragDropWordGame = ({ handleClose, onGameComplete }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const targetWord = wordList[currentIdx].word;
  const allLetters = wordList[currentIdx].letters;
  const imageSrc = wordList[currentIdx].img;

  const [slots, setSlots] = useState(Array(targetWord.length).fill(null));
  const [draggedLetter, setDraggedLetter] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [slotAnim, setSlotAnim] = useState(Array(targetWord.length).fill(false));
  const [scoreAnim, setScoreAnim] = useState(false);
  const [score, setScore] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  // Was `new Audio(...)` called directly in the render body - a fresh
  // pair of Audio elements on every re-render instead of once per game.
  const correctSoundRef = useRef(null);
  const errorSoundRef = useRef(null);
  if (!correctSoundRef.current) correctSoundRef.current = new Audio('/sounds/correct.mp3');
  if (!errorSoundRef.current) errorSoundRef.current = new Audio('/sounds/error.mp3');

  // Load volume from localStorage
  useEffect(() => {
    const savedVolume = localStorage.getItem('tapGameVolume');
    if (savedVolume !== null) setVolume(parseFloat(savedVolume));
  }, []);

  // Announce target word
  useEffect(() => {
    if (!completed && !isMuted) {
      const speakTimeout = setTimeout(() => {
        const utterance = new SpeechSynthesisUtterance(`Form the word ${targetWord.split('').join(' ')}`);
        utterance.rate = 0.8;
        utterance.volume = volume;
        speechSynthesis.speak(utterance);
      }, 400);
      return () => clearTimeout(speakTimeout);
    }
  }, [completed, isMuted, volume, targetWord]);

  // Nothing cancelled in-flight speech on unmount - closing the game
  // mid-instruction kept the voice talking after the modal was gone.
  useEffect(() => {
    return () => speechSynthesis.cancel();
  }, []);

  // Check if word is completed
  useEffect(() => {
    if (slots.join('') === targetWord) {
      setCompleted(true);
      confetti({ particleCount: 80, spread: 60, origin: { y: 0.6 } });
      setScoreAnim(true);
      if (!isMuted) {
        correctSoundRef.current.volume = volume;
        correctSoundRef.current.play();
      }
      setTimeout(() => setScoreAnim(false), 800);

      // If last word, close modal after short delay
      if (currentIdx === wordList.length - 1 && handleClose) {
        setTimeout(() => {
          confetti({ particleCount: 150, spread: 90, origin: { y: 0.5 } });
          // Was missing entirely - the parent's onGameComplete prop (which
          // saves progress via GAME_SAVE, marks the level completed, and
          // shows the "Level Completed!" dialog) was never called here, so
          // finishing all 5 words silently lost the student's progress.
          onGameComplete?.(3, 100);
          handleClose();
        }, 1800);
      }
    }
  }, [slots, isMuted, volume, targetWord, currentIdx, handleClose, onGameComplete]);

  // Drag handlers
  const handleDragStart = (letter) => setDraggedLetter(letter);
  const handleDrop = (idx) => {
    if (!draggedLetter || slots[idx]) return;
    const newSlots = [...slots];
    newSlots[idx] = draggedLetter;
    setSlots(newSlots);

    // Animate slot
    const newAnim = [...slotAnim];
    newAnim[idx] = true;
    setSlotAnim(newAnim);
    setTimeout(() => {
      newAnim[idx] = false;
      setSlotAnim([...newAnim]);
    }, 500);

    // Score logic and sound
    if (!isMuted) {
      if (targetWord[idx] === draggedLetter) {
        correctSoundRef.current.volume = volume;
        correctSoundRef.current.play();
      } else {
        errorSoundRef.current.volume = volume;
        errorSoundRef.current.play();
      }
    }
    if (targetWord[idx] === draggedLetter) {
      setScore((prev) => prev + 1);
    } else {
      setScore((prev) => (prev > 0 ? prev - 1 : 0));
    }

    setDraggedLetter(null);
  };

  const handleDragOver = (e) => e.preventDefault();

  // Letters left to drag
  const availableLetters = allLetters.filter(l => !slots.includes(l));

  // Reset game or go to next word
  const handleReset = () => {
    setSlots(Array(targetWord.length).fill(null));
    setCompleted(false);
    setSlotAnim(Array(targetWord.length).fill(false));
    setScoreAnim(false);
    setScore(0);
  };

  const handleNextWord = () => {
    const nextIdx = (currentIdx + 1) % wordList.length;
    setCurrentIdx(nextIdx);
    setSlots(Array(wordList[nextIdx].word.length).fill(null));
    setCompleted(false);
    setSlotAnim(Array(wordList[nextIdx].word.length).fill(false));
    setScoreAnim(false);
    setScore(0);
  };

  return (
    <div className="tap-letter-board-bg">
      <div className="tap-header-bar mb-1">
        <div className="tap-score-box">
          <span role="img" aria-label="score" style={{ fontSize: 22, marginRight: 6 }}>🏆</span>
          <span className="tap-score-label">Score</span>
          <span className="tap-score-value">{score}/{targetWord.length}</span>
        </div>
        <div className="tap-letter-target">
          <span className="tap-letter-label">Form the word:</span>
          <span className="tap-letter-value">{targetWord}</span>
        </div>
        <div className="tap-volume-box">
          <span role="img" aria-label="volume" style={{ fontSize: 20, marginRight: 4 }}>🔊</span>
          <input
            id="volume"
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => {
              const newVol = parseFloat(e.target.value);
              setVolume(newVol);
              localStorage.setItem('tapGameVolume', newVol);
            }}
            style={{ width: '90px', verticalAlign: 'middle' }}
          />
          <span className="tap-volume-value">{Math.round(volume * 100)}%</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary ms-2"
            style={{ borderRadius: 12, padding: '2px 8px', marginLeft: 8 }}
            title="Replay Word"
            onClick={() => {
              if (!completed && !isMuted) {
                const utterance = new window.SpeechSynthesisUtterance(`Form the word ${targetWord.split('').join(' ')}`);
                utterance.rate = 0.8;
                utterance.volume = volume;
                window.speechSynthesis.speak(utterance);
              }
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 20, verticalAlign: 'middle' }}>replay</span>
          </button>
        </div>
      </div>

      <div className="text-center mb-3">
        <img
          src={imageSrc}
          alt={targetWord}
          style={{
            width: 120,
            height: 120,
            objectFit: 'contain',
            borderRadius: 12,
            background: '#fff',
            border: '2px solid #e3e3e3',
            boxShadow: '0 2px 8px #0001'
          }}
        />
      </div>

      <div className="d-flex mb-3 justify-content-center">
        {slots.map((letter, idx) => (
          <div
            key={idx}
            onDrop={() => handleDrop(idx)}
            onDragOver={handleDragOver}
            className={`border rounded mx-1 text-center ${slotAnim[idx] ? 'pulse' : ''}`}
            style={{
              width: 60,
              height: 60,
              fontSize: 28,
              fontWeight: 'bold',
              background: letter
                ? (letter === targetWord[idx] ? '#e6ffe6' : '#ffe6e6')
                : '#f8f9fa',
              transition: 'background 0.3s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 4px #0001'
            }}
          >
            {letter || <span style={{ color: '#bbb' }}>?</span>}
          </div>
        ))}
      </div>
      <div className="tap-letter-btn-grid" style={{ marginTop: 0 }}>
        {availableLetters.map(letter => (
          <div
            key={letter}
            draggable
            onDragStart={() => handleDragStart(letter)}
            className="letter-btn"
            style={{
              width: 60,
              height: 60,
              fontSize: 28,
              fontWeight: 'bold',
              cursor: 'grab',
              boxShadow: '0 2px 6px #0001',
              background: '#fff',
              userSelect: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 6
            }}
          >
            {letter}
          </div>
        ))}
      </div>
      {completed && (
        <div className="text-center">
          <h4 className="text-success mt-3">🎉 Great job! You formed the word!</h4>
          {currentIdx < wordList.length - 1 ? (
            <>
              <button className="btn btn-primary mt-2 me-2" onClick={handleReset}>
                Replay Word
              </button>
              <button className="btn btn-success mt-2" onClick={handleNextWord}>
                Next Word
              </button>
            </>
          ) : (
            <button className="btn btn-danger mt-2" onClick={handleClose}>
              Close
            </button>
          )}
        </div>
      )}

      <style>
        {`
          .tap-letter-board-bg {
            background: url('/image/board.png'), linear-gradient(135deg,rgba(62, 62, 62, 0.3) 0%,rgba(15, 15, 14, 0.41) 100%);
            background-size: cover, cover;
            background-position: center, center;
            border-radius: 32px;
            box-shadow: 0 6px 32px #e0c68a33;
            padding: 32px 12px 24px 12px;
            margin: 0 auto;
            max-width: 900px;
            width: 98%;
            min-height: 400px;
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
            z-index: 2;
            animation: board-pop 0.5s cubic-bezier(.68,-0.55,.27,1.55);
          }
          @keyframes board-pop {
            0% { transform: scale(0.9); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
          .tap-header-bar {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-radius: 20px;
            box-shadow: 0 2px 8px rgba(224,198,138,0.10);
            padding: 1px 18px 18px 18px;
            margin-bottom: 18px;
            gap: 0;
          }
          .tap-score-box,
          .tap-volume-box {
            display: flex;
            align-items: center;
            border-radius: 14px;
            padding: 6px 14px;
            font-weight: 700;
            font-size: 1.1rem;
            color: #b8860b;
            background: rgba(255, 255, 255, 0.86);
            min-width: 110px;
            box-shadow: 0 1px 4px rgba(224,198,138,0.10);
          }
          .tap-score-label {
            margin-right: 6px;
            font-size: 1rem;
            color: #b8860b;
          }
          .tap-score-value {
            font-size: 1.1rem;
            color: #1976d2;
            margin-left: 4px;
            font-weight: 700;
          }
          .tap-letter-target {
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(224,198,138,0.92);
            border-radius: 18px;
            padding: 8px 32px;
            box-shadow: 0 1px 4px rgba(224,198,138,0.10);
            margin: 0 18px;
            min-width: 180px;
          }
          .tap-letter-label {
            font-size: 1.1rem;
            color: #3e2723;
            margin-right: 8px;
            font-weight: 600;
          }
          .tap-letter-value {
            font-size: 2rem;
            color: #1976d2;
            font-weight: 900;
            letter-spacing: 2px;
            text-shadow: 1px 1px 8px #fff, 0 0 8px #e0c68a;
          }
          .tap-volume-value {
            margin-left: 8px;
            font-size: 1rem;
            color: #1976d2;
            font-weight: 700;
            min-width: 36px;
            display: inline-block;
            text-align: right;
          }
          .tap-letter-btn-grid {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            align-items: center;
            width: 100%;
            min-height: 80px;
            margin-top: 10px;
            gap: 8px;
          }
          .letter-btn {
            padding: 0;
            border-radius: 18px !important;
            background: linear-gradient(135deg,rgb(250, 248, 240) 0%,rgb(248, 228, 166) 100%) !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.10);
            border: 2px solid rgba(223, 214, 192, 0.65) !important;
            min-width: 60px;
            min-height: 60px;
            width: 60px;
            height: 60px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: transform 0.1s, box-shadow 0.2s;
            font-size: 28px;
            font-weight: bold;
          }
          .letter-btn:active, .letter-btn:focus {
            transform: scale(0.96);
            box-shadow: 0 0 0 3px #ffe08255;
          }
          .pulse {
            animation: pulse 0.5s;
          }
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.12); }
            100% { transform: scale(1); }
          }
          /* Responsive styles */
          @media (max-width: 1024px) {
            .tap-letter-board-bg {
              max-width: 99vw;
              padding: 18px 2vw 18px 2vw;
              min-height: 260px;
            }
            .tap-letter-btn-grid {
              min-height: 60px;
            }
            .letter-btn {
              min-width: 48px;
              min-height: 48px;
              width: 48px;
              height: 48px;
              font-size: 22px;
            }
          }
          @media (max-width: 700px) {
            .tap-header-bar {
              flex-direction: column;
              gap: 10px;
              padding-bottom: 10px;
            }
            .tap-letter-target {
              min-width: 0;
              width: 100%;
              padding: 8px 0;
              margin: 8px 0;
            }
            .tap-letter-board-bg {
              padding: 10px 2vw 10px 2vw;
              min-height: 180px;
            }
            .tap-letter-btn-grid {
              min-height: 40px;
              gap: 4px;
            }
            .letter-btn {
              min-width: 36px;
              min-height: 36px;
              width: 36px;
              height: 36px;
              font-size: 16px;
            }
          }
          @media (max-width: 480px) {
            .tap-letter-board-bg {
              padding: 6px 1vw 6px 1vw;
              min-height: 120px;
            }
            .tap-header-bar {
              padding: 6px 4px 6px 4px;
            }
            .tap-letter-btn-grid {
              gap: 2px;
            }
            .letter-btn {
              min-width: 28px;
              min-height: 28px;
              width: 28px;
              height: 28px;
              font-size: 12px;
            }
          }
        `}
      </style>
    </div>
  );
};